import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  useMediaQuery,
  Avatar,
  Typography,
  Box,
  Divider,
  styled,
  Tooltip,
  Badge,
  useTheme,
  Chip,
  alpha,
  Paper
} from '@mui/material';
import {
  Home,
  Analytics,
  Group,
  PointOfSale,
  Restaurant,
  ShoppingCart,
  Business,
  Receipt,
  Description,
  Settings,
  Help,
  Logout,
  Menu,
  People,
  ExpandMore,
  ExpandLess,
  Storefront,
  LocalAtm,
  Inventory,
  DashboardCustomize,
  Person
} from '@mui/icons-material';

// Clean black sidebar container
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 260,
  background: '#000000',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid #244489',
  boxShadow: '2px 0 8px rgba(30, 17, 152, 0.3)',
  transition: 'all 0.2s ease',
  color: '#FFFFFF',
  position: 'relative',
}));

// Menu item with black theme
const MenuItem = styled(ListItem)(({ theme, selected }) => ({
  borderRadius: '8px',
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1, 1.5),
  transition: 'all 0.15s ease',
  backgroundColor: selected ? '#1F1F1F' : 'transparent',
  '&:hover': {
    backgroundColor: '#21a0c0',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: selected ? '#FFFFFF' : '#808080',
  },
  '& .MuiListItemText-primary': {
    fontWeight: selected ? 500 : 400,
    color: selected ? '#FFFFFF' : '#CCCCCC',
    fontSize: '0.9rem',
  }
}));

// Quick access chip for black theme
const QuickAccessChip = styled(Chip)(({ color }) => ({
  backgroundColor: '#1F1F1F',
  color: '#FFFFFF',
  fontSize: '0.75rem',
  height: 28,
  fontWeight: 500,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#2D2D2D',
  },
}));

const menuItems = [
  { 
    to: '/Home', 
    icon: <DashboardCustomize />, 
    label: 'Tableau de bord',
  },
  { 
    to: '/Analytics', 
    icon: <Analytics />, 
    label: 'Analytique',
  },
  {
    icon: <LocalAtm />,
    label: 'Caisse',
    to: '/vault',
    children: [
      { to: '/vault', label: 'Vue d\'ensemble' },
      { to: '/Encaissement', label: 'Encaissements' },
      { to: '/Decaissement', label: 'Décaissements' }
    ]
  },
  { 
    to: '/rh', 
    icon: <Group />, 
    label: 'Ressources Humaines',
  },

  {
    icon: <Inventory />,
    label: 'Stock',
    to: '/Stock',
    children: [
      { to: '/Stock', label: 'Vue d\'ensemble' },
      { to: '/Inventaire', label: 'Inventaire' },
      { to: '/Achat', label: 'Achat' }
    ]
  },
  {
    icon: <Restaurant />,
    label: 'Food Cost',
    to: '/foodcost',
    children: [
      { to: '/foodcost', label: 'Fiche Technique' },
      { to: '/DailyServings', label: 'Registre des Ventes' },
    ]
  },
  { 
    to: '/fournisseurs', 
    icon: <Business />, 
    label: 'Fournisseurs',
  },
  { 
    to: '/Partenaire', 
    icon: <People />, 
    label: 'Partenaires',
  },
  { 
    to: '/MarketPlace', 
    icon: <Storefront />, 
    label: 'SupperMarché',
  },
  { 
    to: '/IntegrationCaisse', 
    icon: <PointOfSale />, 
    label: 'Intégration Caisse',
  },
  { 
    to: '/Facture', 
    icon: <Receipt />, 
    label: 'Factures',
  },
  { 
    to: '/Formulaire', 
    icon: <Description />, 
    label: 'Déclarations',
  }
];

const quickAccess = [
  { to: '/DailyServingsAnalytics', label: 'FC-Analytique' },
  { to: '/rh', label: 'RH' },
  { to: '/vault', label: 'Caisse' }
];



function Sidebar({ onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [isOpen, setIsOpen] = useState(!isMobile);
  const [openModules, setOpenModules] = useState({});
  const user = JSON.parse(localStorage.getItem("user"));

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onToggle) onToggle(newState);
  };

  const toggleModule = (label) => {
    setOpenModules(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate('/login');
  };

  return (
    <>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{
          width: isOpen ? 260 : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 260,
            boxSizing: 'border-box',
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <SidebarContainer>
          {/* Header with logo */}
          <Box sx={{ 
            p: 2.5, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            borderBottom: '1px solid #1F1F1F'
          }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#FFFFFF',
                color: '#000000',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              ERP
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#FFFFFF', lineHeight: 1.2 }}>
                Optimizi
              </Typography>
              <Typography variant="caption" sx={{ color: '#808080' }}>
                v2.0 • Enterprise
              </Typography>
            </Box>
          </Box>

          {/* User Profile */}
          <Box sx={{ p: 2 }}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: '#0A0A0A',
                borderRadius: 2,
                p: 1.5,
                border: '1px solid #1F1F1F',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: '#FFFFFF',
                    color: '#000000',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }}
                >
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                    {user?.prenom} {user?.nom}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#808080' }}>
                    {user?.role || 'Administrateur'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Quick Access */}
          <Box sx={{ px: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#666666', ml: 1, fontSize: '0.7rem', fontWeight: 500 }}>
              RACCOURCIS
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
              {quickAccess.map((item) => (
                <QuickAccessChip
                  key={item.label}
                  label={item.label}
                  clickable
                  component={Link}
                  to={item.to}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#1F1F1F', mx: 2 }} />

          {/* Main Navigation */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
            <List>
              {menuItems.map((item) => (
                item.children ? (
                  <React.Fragment key={item.label}>
                    <MenuItem
                      button
                      onClick={() => toggleModule(item.label)}
                      selected={openModules[item.label]}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                      {openModules[item.label] ? 
                        <ExpandLess sx={{ fontSize: 20, color: '#808080' }} /> : 
                        <ExpandMore sx={{ fontSize: 20, color: '#808080' }} />
                      }
                    </MenuItem>
                    <Collapse in={openModules[item.label]} timeout="auto">
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <MenuItem
                            key={child.to}
                            button
                            component={Link}
                            to={child.to}
                            selected={isActive(child.to)}
                            sx={{ pl: 4 }}
                          >
                            <Box sx={{ width: 4, height: 4, bgcolor: '#808080', borderRadius: '50%', mr: 2 }} />
                            <ListItemText primary={child.label} />
                          </MenuItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ) : (
                  <MenuItem
                    key={item.to}
                    button
                    component={Link}
                    to={item.to}
                    selected={isActive(item.to)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </MenuItem>
                )
              ))}
            </List>
          </Box>

          {/* Bottom Section */}
          <Box sx={{ borderTop: '1px solid #1F1F1F', p: 1 }}>
            <List>
             
              {/* Logout */}
              <MenuItem
                button
                onClick={handleLogout}
                sx={{
                  '&:hover': {
                    backgroundColor: '#2D1A1A',
                    '& .MuiListItemIcon-root': { color: '#FF4444' },
                    '& .MuiListItemText-primary': { color: '#FF4444' }
                  }
                }}
              >
                <ListItemIcon><Logout /></ListItemIcon>
                <ListItemText primary="Déconnexion" />
              </MenuItem>
            </List>
          </Box>
        </SidebarContainer>
      </Drawer>

      {/* Toggle Button */}
      <Tooltip title={isOpen ? "Fermer le menu" : "Ouvrir le menu"}>
        <IconButton
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: isOpen ? 280 : 16,
            zIndex: 1300,
            bgcolor: '#000000',
            color: '#FFFFFF',
            width: 36,
            height: 36,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            border: '1px solid #1F1F1F',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#1A1A1A',
              transform: 'scale(1.05)',
            }
          }}
        >
          <Menu sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </>
  );
}

export default Sidebar;